import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Upload, CheckCircle2, Loader2, X, Phone } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { usePageMeta, PageHero } from "../components/Shared";
import { SERVICES, COMPANY, IMAGES } from "../data/site";

const inputCls =
  "w-full bg-white border border-border px-4 py-3 text-charcoal placeholder:text-gray-400 focus:outline-none focus:border-orange focus:ring-2 focus:ring-orange/30 transition-shadow rounded-none";

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="text-sm font-semibold text-navy">{label}{required && <span className="text-orange"> *</span>}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);

const EMPTY = {
  full_name: "", company_name: "", email: "", phone: "", service_required: "",
  pickup_location: "", delivery_location: "", freight_type: "", num_pallets: "",
  total_weight: "", freight_dimensions: "", expected_arrival_date: "", storage_duration: "",
  special_instructions: "", preferred_contact: "Email", additional_details: "", website: "",
};

export default function Quote() {
  usePageMeta("Request a Quote", "Request a cross-docking, warehousing, or freight handling quote from CoreDock Logistics in Mississauga.");
  const [params] = useSearchParams();
  const [form, setForm] = useState({ ...EMPTY, service_required: params.get("service") || "" });
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onFiles = async (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    setUploading(true);
    for (const file of picked) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setFiles((prev) => [...prev, data]);
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || `Could not upload ${file.name}`);
      }
    }
    setUploading(false);
    e.target.value = "";
  };

  const removeFile = (id) => setFiles((prev) => prev.filter((f) => f.id !== id));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email, and phone number.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", { ...form, lead_type: "quote", files });
      setDone(true);
      toast.success("Request received — we'll be in touch shortly.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <>
        <PageHero eyebrow="Request a Quote" title="Request Received" image={IMAGES.warehouse} />
        <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="quote-success">
          <CheckCircle2 size={64} className="text-orange mx-auto" strokeWidth={1.5} />
          <h2 className="font-display font-bold text-3xl mt-6">Thank you for contacting CoreDock Logistics.</h2>
          <p className="mt-4 text-text-secondary leading-relaxed">
            We have received your request and will contact you shortly.
          </p>
          <a href={`tel:${COMPANY.phoneRaw}`} className="btn-orange inline-flex items-center gap-2 px-6 py-3 mt-8 font-semibold uppercase tracking-wide text-sm">
            <Phone size={16} /> Or call us now
          </a>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHero eyebrow="Request a Quote" image={IMAGES.warehouse}
        title="Tell Us About Your Freight"
        subtitle="Share your shipment details and our team will respond with a practical logistics solution." />

      <div className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <form onSubmit={submit} data-testid="quote-form" className="bg-white border border-border p-6 md:p-10 space-y-6">
          {/* honeypot */}
          <input type="text" value={form.website} onChange={set("website")} name="website"
                 tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />

          <div className="grid sm:grid-cols-2 gap-6">
            <Field label="Full Name" required><input data-testid="q-full-name" className={inputCls} value={form.full_name} onChange={set("full_name")} placeholder="Jane Smith" /></Field>
            <Field label="Company Name"><input data-testid="q-company" className={inputCls} value={form.company_name} onChange={set("company_name")} placeholder="Acme Freight Inc." /></Field>
            <Field label="Email" required><input data-testid="q-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="jane@company.com" /></Field>
            <Field label="Phone Number" required><input data-testid="q-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="647-000-0000" /></Field>
          </div>

          <Field label="Service Required">
            <select data-testid="q-service" className={inputCls} value={form.service_required} onChange={set("service_required")}>
              <option value="">Select a service</option>
              {SERVICES.map((s) => <option key={s.slug} value={s.title}>{s.title}</option>)}
            </select>
          </Field>

          <div className="grid sm:grid-cols-2 gap-6">
            <Field label="Pickup Location"><input data-testid="q-pickup" className={inputCls} value={form.pickup_location} onChange={set("pickup_location")} placeholder="City / address" /></Field>
            <Field label="Delivery Location"><input data-testid="q-delivery" className={inputCls} value={form.delivery_location} onChange={set("delivery_location")} placeholder="City / address" /></Field>
            <Field label="Freight Type"><input data-testid="q-freight-type" className={inputCls} value={form.freight_type} onChange={set("freight_type")} placeholder="e.g. palletized dry goods" /></Field>
            <Field label="Number of Pallets"><input data-testid="q-pallets" className={inputCls} value={form.num_pallets} onChange={set("num_pallets")} placeholder="e.g. 6" /></Field>
            <Field label="Total Weight"><input data-testid="q-weight" className={inputCls} value={form.total_weight} onChange={set("total_weight")} placeholder="e.g. 2,400 lbs" /></Field>
            <Field label="Freight Dimensions"><input data-testid="q-dimensions" className={inputCls} value={form.freight_dimensions} onChange={set("freight_dimensions")} placeholder="L × W × H" /></Field>
            <Field label="Expected Arrival Date"><input data-testid="q-arrival" type="date" className={inputCls} value={form.expected_arrival_date} onChange={set("expected_arrival_date")} /></Field>
            <Field label="Storage Duration (if required)"><input data-testid="q-storage" className={inputCls} value={form.storage_duration} onChange={set("storage_duration")} placeholder="e.g. 24–72 hours" /></Field>
          </div>

          <Field label="Special Handling Instructions">
            <textarea data-testid="q-special" rows={3} className={inputCls} value={form.special_instructions} onChange={set("special_instructions")} placeholder="Fragile, temperature, stacking, labelling…" />
          </Field>

          <Field label="Preferred Contact Method">
            <select data-testid="q-contact-method" className={inputCls} value={form.preferred_contact} onChange={set("preferred_contact")}>
              <option>Email</option><option>Phone</option><option>WhatsApp</option>
            </select>
          </Field>

          <Field label="Additional Details">
            <textarea data-testid="q-additional" rows={3} className={inputCls} value={form.additional_details} onChange={set("additional_details")} placeholder="Anything else we should know?" />
          </Field>

          {/* File upload */}
          <div>
            <span className="text-sm font-semibold text-navy">Shipment Documents or Freight Photos</span>
            <label className="mt-1.5 flex items-center justify-center gap-3 border-2 border-dashed border-border hover:border-orange transition-colors p-6 cursor-pointer">
              {uploading ? <Loader2 className="animate-spin text-orange" /> : <Upload className="text-orange" strokeWidth={1.5} />}
              <span className="text-sm text-text-secondary">{uploading ? "Uploading…" : "Click to upload (PDF, JPG, PNG — max 10MB each)"}</span>
              <input data-testid="q-file-input" type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx" onChange={onFiles} className="hidden" />
            </label>
            {files.length > 0 && (
              <ul className="mt-3 space-y-2" data-testid="q-file-list">
                {files.map((f) => (
                  <li key={f.id} className="flex items-center justify-between bg-offwhite border border-border px-4 py-2 text-sm">
                    <span className="truncate">{f.original_filename}</span>
                    <button type="button" onClick={() => removeFile(f.id)} className="text-gray-400 hover:text-orange"><X size={16} /></button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <button type="submit" data-testid="q-submit" disabled={submitting}
                  className="btn-orange w-full py-4 font-semibold uppercase tracking-wide flex items-center justify-center gap-2 disabled:opacity-60">
            {submitting ? <><Loader2 className="animate-spin" size={18} /> Sending…</> : "Submit Request"}
          </button>
          <p className="text-xs text-gray-400 text-center">Your information is used only to respond to your request. Protected by spam filtering.</p>
        </form>
      </div>
    </>
  );
}
